import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from 'recharts'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (node 524:3257 — Radar Chart):
 *
 * 9 variants:
 *   Basic             — Single series, polygon grid, filled area
 *   Dots              — Single series with dot markers
 *   Multiple          — Two series overlaid
 *   Lines Only        — Two series, stroke only (no fill)
 *   Grid Custom       — Custom polygon grid shape
 *   Grid Filled       — Filled background on grid
 *   Grid Circle       — Circular grid lines
 *   No Lines          — Filled area, no grid lines visible
 *   Grid Circle Filled — Circular grid + filled area
 *
 * Specs:
 *   Colors: --chart-1 through --chart-5
 *   Fill opacity: ~0.6 for area
 *   Dots: r=4, strokeWidth=2
 *   Grid: stroke-muted-foreground, polygon or circle
 *   Angle axis: text-xs text-muted-foreground
 */

export type RadarGridType = 'polygon' | 'circle'

export interface IntuiRadarChartProps {
  /** Chart data array — each entry needs an axis label key + value keys */
  data: Record<string, unknown>[]
  /** Chart configuration (keys, labels, colors) */
  config: ChartConfig
  /** Data keys to render as radar areas (defaults to all config keys) */
  dataKeys?: string[]
  /** Axis label data key */
  axisKey?: string
  /** Grid shape */
  gridType?: RadarGridType
  /** Show grid lines */
  showGrid?: boolean
  /** Fill the grid background */
  gridFill?: boolean
  /** Show filled area (false = lines only) */
  showFill?: boolean
  /** Fill opacity for radar areas */
  fillOpacity?: number
  /** Show dots on data points */
  showDots?: boolean
  /** Dot radius */
  dotRadius?: number
  /** Show angle axis labels */
  showAxisLabels?: boolean
  /** Show radius axis */
  showRadiusAxis?: boolean
  /** Show chart legend */
  showLegend?: boolean
  /** Show tooltip */
  showTooltip?: boolean
  /** Additional className */
  className?: string
}

function IntuiRadarChart({
  data,
  config,
  dataKeys,
  axisKey = 'month',
  gridType = 'polygon',
  showGrid = true,
  gridFill = false,
  showFill = true,
  fillOpacity = 0.6,
  showDots = false,
  dotRadius = 4,
  showAxisLabels = true,
  showRadiusAxis = false,
  showLegend = false,
  showTooltip = true,
  className,
}: IntuiRadarChartProps) {
  const keys = dataKeys ?? Object.keys(config)

  return (
    <ChartContainer config={config} className={cn('mx-auto aspect-square h-[250px]', className)}>
      <RadarChart data={data}>
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent />}
          />
        )}
        {showGrid && (
          <PolarGrid
            gridType={gridType}
            className={gridFill ? 'fill-muted opacity-50' : undefined}
          />
        )}
        {showAxisLabels && (
          <PolarAngleAxis dataKey={axisKey} className="text-xs" />
        )}
        {showRadiusAxis && (
          <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
        )}
        {keys.map((key) => (
          <Radar
            key={key}
            dataKey={key}
            fill={`var(--color-${key})`}
            fillOpacity={showFill ? fillOpacity : 0}
            stroke={`var(--color-${key})`}
            dot={showDots ? { r: dotRadius, strokeWidth: 2 } : false}
          />
        ))}
        {showLegend && (
          <ChartLegend
            content={({ payload, verticalAlign }) => (
              <ChartLegendContent
                payload={payload}
                verticalAlign={verticalAlign}
              />
            )}
          />
        )}
      </RadarChart>
    </ChartContainer>
  )
}
IntuiRadarChart.displayName = 'IntuiRadarChart'

export { IntuiRadarChart }
export type { ChartConfig }
